import styled, { css } from 'styled-components';
import { handleFileDownload } from '../../../../utils/file-download';
import { TypographyVariant } from '../../../../utils/theme';
import { getIsImageFile } from '../../../file-uploader-and-list/file-uploader-and-list';
import Document from '../../../icon/document';
import { InlineTextButton } from '../../../inline-text-button/inline-text-button';
import Typography from '../../../typography/typography';
import type { RJSFFile, SchemaFormValuesCopy } from '../types';
import { getFile } from '../utils';
import VisibilityInformation from './visibility-information';

interface FileProps {
  value: RJSFFile | string;
  copy: SchemaFormValuesCopy;
  name?: string;
  description?: string;
  isPolicyDocument?: boolean;
}

interface FileDownloadButtonProps extends FileImageProps {
  copy: SchemaFormValuesCopy;
  description?: string;
  isPolicyDocument?: boolean;
}

interface FileImageProps {
  label: string;
  url: string;
}

export const File = ({
  value,
  copy,
  name,
  description,
  isPolicyDocument,
}: FileProps) => {
  const fileValues = getFile(value);
  const { file, originalName } = fileValues;
  return getIsImageFile(fileValues) ? (
    <FileImage label={originalName} url={file} />
  ) : (
    <FileDownloadButton
      label={name ?? originalName}
      url={file}
      copy={copy}
      description={description}
      isPolicyDocument={isPolicyDocument}
    />
  );
};

const FileDownloadButton = ({
  label,
  url,
  copy,
  description,
  isPolicyDocument,
}: FileDownloadButtonProps) => {
  const onClick = () =>
    handleFileDownload({
      url,
      filename: label,
      successMessage: copy.downloadSuccess,
      errorMessage: copy.downloadError,
    });
  return (
    <FileWrapper>
      <FileLinkContainer>
        <Document />
        <InlineTextButton label={label} onClick={onClick} />
      </FileLinkContainer>
      {description && (
        <Typography variant={TypographyVariant.bodyMd1}>
          {description}
        </Typography>
      )}
      <VisibilityInformation isPolicyDocument={isPolicyDocument} />
    </FileWrapper>
  );
};

const FileImage = ({ label, url }: FileImageProps) => {
  return <ImagePreview src={url} alt={label} />;
};

export default File;

const FileWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `}
`;

const FileLinkContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(0.5)};
    align-items: center;
  `}
`;

const ImagePreview = styled.img`
  width: 100px;
  height: 100px;
  object-fit: cover;
`;
